import Vue from 'vue'
import Snackbar from '../../components/Snackbar'
import moment from 'moment'

const getDefaultState = () => ({
  isLoading: false,
  restrictedRecords: [],
  filteredColumnList: [],
  limit: 10,
  offset: 0,
  searchQuery: '',
  totalRecords: 0,
  filter: null,
  selectedItemsMap: {}, // Separate state for selections
  isFileDownloading: false
})

export default {
  namespaced: true,
  state: getDefaultState(),
  actions: {
    async getRestrictedRecords({ commit, state }) {
      commit('SET_LOADING', true)
      try {
        const response = await Vue.prototype.$http.post(
          '/dmp/restricted-entities',
          {
            limit: state.limit,
            offset: state.offset,
            searchQuery: state.searchQuery,
            filterOptions: state.filter?.filters
          }
        )
        if (response && response.data.status === 200) {
          const dataObj = response.data.data
          const { data = [], totalRows = 0, limit = 10, offset = 0 } = dataObj
          commit('SET_LIMIT', limit)
          commit('SET_OFFSET', offset)
          commit('SET_TOTAL_RECORDS', totalRows)
          commit('SET_RESTRICTED_RECORDS', data)
        } else if (response && response.data.status === 404) {
          // Reset the state if no records are found
          commit('SET_RESTRICTED_RECORDS', [])
          commit('SET_TOTAL_RECORDS', 0)
        }
      } catch (err) {
        Snackbar({
          message: 'Failed to fetch the records',
          type: 'is-danger',
          indefinite: true
        })
      } finally {
        commit('SET_LOADING', false)
      }
    },
    async downloadRestricteRecordsData({ commit, state }) {
      commit('SET_FILE_DOWNLOADING_FLAG', true)
      try {
        const response = await Vue.prototype.$http.post(
          '/dmp/restricted-entities/download',
          {
            limit: state.limit,
            offset: state.offset,
            searchQuery: state.searchQuery,
            filterOptions: state.filter?.filters
          },
          {
            responseType: 'blob' // Ensure response is treated as a blob
          }
        )

        const blob = new Blob([response.data], { type: 'text/csv' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `restricted_entities_${moment().format(
          'DD-MM-YY_HH:mm'
        )}.csv`
        link.click()
        Snackbar({
          message: 'File downloaded successfully',
          type: 'is-success'
        })
      } catch (err) {
        console.error('Error downloading CSV:', err)
        Snackbar({
          message: 'Failed to download the CSV',
          type: 'is-danger'
        })
      } finally {
        commit('SET_FILE_DOWNLOADING_FLAG', false)
      }
    },
    addFilteredColumnList({ commit, state }, columnObj) {
      const { columnName, isFilter } = columnObj
      let columnList = [...state.filteredColumnList]
      if (isFilter) {
        columnList = [...columnList, columnName]
      } else {
        const index = columnList.indexOf(columnName)
        if (index > -1) {
          columnList.splice(index, 1)
        }
      }
      columnList = [...new Set(columnList)]
      commit('SET_FILTERED_COLUMN_LIST', columnList)
    },
    updateFilteredColumnList({ commit }, filteredColumnList) {
      commit('SET_FILTERED_COLUMN_LIST', filteredColumnList)
    },
    updateTotalRecords({ commit }, totalRecords) {
      commit('SET_TOTAL_RECORDS', totalRecords)
    },
    async updatePagination({ commit, dispatch }, { limit, offset }) {
      commit('SET_LIMIT', limit)
      commit('SET_OFFSET', offset)
      await dispatch('getRestrictedRecords')
    },
    async updatePaginationAndFilter(
      { commit, dispatch },
      { limit, offset, filter }
    ) {
      commit('SET_LIMIT', limit)
      commit('SET_OFFSET', offset)
      commit('SET_FILTER', filter)
      await dispatch('getRestrictedRecords')
    },
    async updateSearchQuery({ commit, dispatch }, searchQuery) {
      commit('SET_SEARCH_QUERY', searchQuery)
      commit('SET_OFFSET', 0) // Reset offset to 0
      await dispatch('getRestrictedRecords')
    },
    async updateFilter({ commit, dispatch }, filter) {
      commit('SET_FILTER', filter)
      commit('SET_OFFSET', 0) // Reset pagination when applying a filter
      await dispatch('getRestrictedRecords') // Fetch filtered data
    },
    clearSearchFiltersPagination({ commit }) {
      commit('SET_SEARCH_QUERY', '')
      commit('SET_LIMIT', 10)
      commit('SET_OFFSET', 0)
      commit('SET_FILTER', null)
    },
    updateSelectedItems({ commit, state }, { itemId, isSelected }) {
      const updatedMap = { ...state.selectedItemsMap }

      if (isSelected) {
        updatedMap[itemId] = true
      } else {
        delete updatedMap[itemId]
      }

      commit('SET_SELECTED_ITEMS_MAP', updatedMap)
    },

    toggleAllSelections({ commit, state }, isSelected) {
      const updatedMap = {}

      // Apply selection to all currently visible records
      const updatedRecords = state.restrictedRecords.map((item) => {
        updatedMap[item.sgaId] = isSelected // Track selection
        return { ...item, selected: isSelected } // Ensure selected property is updated
      })

      commit('SET_SELECTED_ITEMS_MAP', updatedMap)
      commit('SET_RESTRICTED_RECORDS', updatedRecords) // Update grid data
    }
  },
  mutations: {
    SET_LOADING(state, bool) {
      state.isLoading = bool
    },
    SET_RESTRICTED_RECORDS(state, records) {
      state.restrictedRecords = records
    },
    SET_FILTERED_COLUMN_LIST(state, filteredColumnList) {
      state.filteredColumnList = filteredColumnList
    },
    SET_TOTAL_RECORDS(state, totalRecords) {
      state.totalRecords = totalRecords
    },
    SET_LIMIT(state, limit) {
      state.limit = limit
    },
    SET_OFFSET(state, offset) {
      state.offset = offset
    },
    SET_SEARCH_QUERY(state, searchQuery) {
      state.searchQuery = searchQuery
    },
    SET_SELECTED_ITEMS_MAP(state, selectedItemsMap) {
      state.selectedItemsMap = selectedItemsMap
    },
    SET_FILTER(state, filter) {
      state.filter = filter
    },
    SET_FILE_DOWNLOADING_FLAG(state, isFileDownloading) {
      state.isFileDownloading = isFileDownloading
    }
  }
}
